export interface PendingMapCreation {
  id: string;
  topic: string;
}

export interface PendingBranchCreation {
  id: string;
  mapId: string;
  parentId: string | null;
  label: string;
}

export interface PendingCreations {
  map: PendingMapCreation | null;
  branch: PendingBranchCreation | null;
}

type StoredPendingCreation =
  | { accountId: string; kind: 'map'; value: PendingMapCreation }
  | { accountId: string; kind: 'branch'; value: PendingBranchCreation };

const LEGACY_STORAGE_KEY = 'pn_realize_pending_creations';
const STORAGE_PREFIX = 'pn_realize_pending_creation:';
const EMPTY: PendingCreations = { map: null, branch: null };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function mapCreation(value: unknown): PendingMapCreation | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === 'string' &&
    UUID.test(candidate.id) &&
    typeof candidate.topic === 'string'
    ? { id: candidate.id, topic: candidate.topic }
    : null;
}

function branchCreation(value: unknown): PendingBranchCreation | null {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.id === 'string' &&
    UUID.test(candidate.id) &&
    typeof candidate.mapId === 'string' &&
    candidate.mapId !== '' &&
    (candidate.parentId === null ||
      (typeof candidate.parentId === 'string' && candidate.parentId !== '')) &&
    typeof candidate.label === 'string'
    ? {
        id: candidate.id,
        mapId: candidate.mapId,
        parentId: candidate.parentId as string | null,
        label: candidate.label,
      }
    : null;
}

function storageKey(accountId: string, kind: StoredPendingCreation['kind'], id: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(accountId)}:${kind}:${id}`;
}

function storedCreation(serialized: string, accountId: string): StoredPendingCreation | null {
  try {
    const value = JSON.parse(serialized) as unknown;
    if (typeof value !== 'object' || value === null) return null;
    const candidate = value as Record<string, unknown>;
    if (candidate.accountId !== accountId) return null;
    if (candidate.kind === 'map') {
      const map = mapCreation(candidate.value);
      return map === null ? null : { accountId, kind: 'map', value: map };
    }
    if (candidate.kind === 'branch') {
      const branch = branchCreation(candidate.value);
      return branch === null ? null : { accountId, kind: 'branch', value: branch };
    }
    return null;
  } catch {
    return null;
  }
}

function legacyCreations(serialized: string, accountId: string): PendingCreations | null {
  try {
    const value = JSON.parse(serialized) as unknown;
    if (typeof value !== 'object' || value === null) return null;
    const candidate = value as Record<string, unknown>;
    if (candidate.accountId !== accountId) return null;
    return {
      map: mapCreation(candidate.map),
      branch: branchCreation(candidate.branch),
    };
  } catch {
    return null;
  }
}

export function readPendingCreations(accountId: string): PendingCreations {
  const maps = new Map<string, PendingMapCreation>();
  const branches = new Map<string, PendingBranchCreation>();
  try {
    const keys = Array.from({ length: window.localStorage.length }, (_, index) =>
      window.localStorage.key(index),
    );
    for (const key of keys) {
      if (key === null || !key.startsWith(STORAGE_PREFIX)) continue;
      const serialized = window.localStorage.getItem(key);
      if (serialized === null) continue;
      const stored = storedCreation(serialized, accountId);
      if (stored?.kind === 'map') {
        maps.set(stored.value.id, stored.value);
      } else if (stored?.kind === 'branch') {
        branches.set(stored.value.id, stored.value);
      }
    }
  } catch {
    return EMPTY;
  }

  try {
    const serialized = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    const legacy = serialized === null ? null : legacyCreations(serialized, accountId);
    if (legacy !== null) {
      if (legacy.map !== null) maps.set(legacy.map.id, legacy.map);
      if (legacy.branch !== null) branches.set(legacy.branch.id, legacy.branch);
      if (
        writePendingMapCreation(accountId, null, legacy.map) &&
        writePendingBranchCreation(accountId, null, legacy.branch)
      ) {
        window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      }
    }
  } catch {
    return {
      map: [...maps.values()].sort((left, right) => left.id.localeCompare(right.id))[0] ?? null,
      branch:
        [...branches.values()].sort((left, right) => left.id.localeCompare(right.id))[0] ?? null,
    };
  }

  return {
    map: [...maps.values()].sort((left, right) => left.id.localeCompare(right.id))[0] ?? null,
    branch:
      [...branches.values()].sort((left, right) => left.id.localeCompare(right.id))[0] ?? null,
  };
}

export function writePendingMapCreation(
  accountId: string,
  previous: PendingMapCreation | null,
  value: PendingMapCreation | null,
): boolean {
  try {
    if (value !== null) {
      const stored: StoredPendingCreation = { accountId, kind: 'map', value };
      window.localStorage.setItem(storageKey(accountId, 'map', value.id), JSON.stringify(stored));
    }
    if (previous !== null && previous.id !== value?.id) {
      window.localStorage.removeItem(storageKey(accountId, 'map', previous.id));
    }
    return true;
  } catch {
    return false;
  }
}

export function writePendingBranchCreation(
  accountId: string,
  previous: PendingBranchCreation | null,
  value: PendingBranchCreation | null,
): boolean {
  try {
    if (value !== null) {
      const stored: StoredPendingCreation = { accountId, kind: 'branch', value };
      window.localStorage.setItem(
        storageKey(accountId, 'branch', value.id),
        JSON.stringify(stored),
      );
    }
    if (previous !== null && previous.id !== value?.id) {
      window.localStorage.removeItem(storageKey(accountId, 'branch', previous.id));
    }
    return true;
  } catch {
    return false;
  }
}
